/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMeasure } from '../use-measure/use-measure.hook';

interface FadeState {
  showTop: boolean;
  showBottom: boolean;
  showLeft: boolean;
  showRight: boolean;
}

interface UseScrollFadesOptions {
  /** Distance in pixels from edge before fades appear/disappear. Default: 8 */
  threshold?: number;
  /** Size of fade effect in pixels for mask-image approach. Default: 20 */
  fadeSize?: number;
  /** Duration of fade transition in milliseconds. Default: 200 */
  transitionDuration?: number;
}

function prefersReducedMotion(): boolean {
  // Server-side rendering safety check
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false;
  }

  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    // Fallback for browsers that don't support matchMedia
    return false;
  }
}

function prefersHighContrast(): boolean {
  // Server-side rendering safety check
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false;
  }

  try {
    // Check for various high contrast media queries
    return (
      window.matchMedia('(prefers-contrast: high)').matches ||
      window.matchMedia('(-ms-high-contrast: active)').matches ||
      window.matchMedia('(-ms-high-contrast: white-on-black)').matches ||
      window.matchMedia('(-ms-high-contrast: black-on-white)').matches
    );
  } catch {
    return false;
  }
}

function supportsMaskImage(): boolean {
  // Server-side rendering safety check
  if (typeof window === 'undefined' || !window.CSS || !window.CSS.supports) {
    return false;
  }

  try {
    // Check for standard and webkit-prefixed mask-image support
    return (
      window.CSS.supports('mask-image', 'linear-gradient(black, transparent)') ||
      window.CSS.supports('-webkit-mask-image', 'linear-gradient(black, transparent)')
    );
  } catch {
    return false;
  }
}

function supportsTransitions(): boolean {
  // Server-side rendering safety check
  if (typeof window === 'undefined' || !window.CSS || !window.CSS.supports) {
    return false;
  }

  try {
    return window.CSS.supports('transition', 'opacity 0.3s ease');
  } catch {
    return false;
  }
}

function getMotionPreference(): 'reduce' | 'no-preference' | 'unknown' {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return 'unknown';
  }

  try {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return 'reduce';
    }
    if (window.matchMedia('(prefers-reduced-motion: no-preference)').matches) {
      return 'no-preference';
    }
    return 'unknown';
  } catch {
    return 'unknown';
  }
}

function watchMotionPreference(callback: (prefersReduced: boolean) => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return () => void 0;
  }

  try {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const handleChange = (e: MediaQueryListEvent) => callback(e.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      }
    };
  } catch {
    return () => void 0;
  }
}

/** Browser and accessibility capabilities check */
function getBrowserCapabilities() {
  return {
    // Accessibility preferences
    prefersReducedMotion: prefersReducedMotion(),
    prefersHighContrast: prefersHighContrast(),
    motionPreference: getMotionPreference(),

    // CSS feature support
    supportsMaskImage: supportsMaskImage(),
    supportsTransitions: supportsTransitions(),

    // Browser environment
    isSSR: typeof window === 'undefined',
    hasMatchMedia: typeof window !== 'undefined' && !!window.matchMedia,
    hasCSSSupports: typeof window !== 'undefined' && !!window.CSS?.supports,
  };
}

const computeFadeState = (
  scrollTop: number,
  scrollHeight: number,
  clientHeight: number,
  scrollLeft: number,
  scrollWidth: number,
  clientWidth: number,
  threshold = 8,
): FadeState => {
  const atTop = scrollTop <= threshold;
  const atBottom = scrollTop + clientHeight >= scrollHeight - threshold;
  const atLeft = scrollLeft <= threshold;
  const atRight = scrollLeft + clientWidth >= scrollWidth - threshold;

  return {
    showTop: !atTop,
    showBottom: !atBottom,
    showLeft: !atLeft,
    showRight: !atRight,
  };
};

/** Creates mask-image CSS value for fade effects */
const createMaskImage = (
  showTop: boolean,
  showBottom: boolean,
  showLeft: boolean,
  showRight: boolean,
  fadeSize = 20,
): string => {
  // Create base mask (fully opaque center)
  const topStop = showTop ? `${fadeSize}px` : '0px';
  const bottomStop = showBottom ? `calc(100% - ${fadeSize}px)` : '100%';
  const leftStop = showLeft ? `${fadeSize}px` : '0px';
  const rightStop = showRight ? `calc(100% - ${fadeSize}px)` : '100%';

  // Vertical gradient (handles top/bottom fades)
  const verticalGradient = `linear-gradient(to bottom, transparent 0px, black ${topStop}, black ${bottomStop}, transparent 100%)`;

  // Horizontal gradient (handles left/right fades)
  const horizontalGradient = `linear-gradient(to right, transparent 0px, black ${leftStop}, black ${rightStop}, transparent 100%)`;

  return `${verticalGradient}, ${horizontalGradient}`;
};

/** Computes container styles with mask-based fade effects and colored gradients */
const computeContainerStyles = (params: {
  showTop: boolean;
  showBottom: boolean;
  showLeft: boolean;
  showRight: boolean;
  fadeSize: number;
  disableTransitions: boolean;
  transitionDuration: number;
  shouldApplyEffects: boolean;
  maskImageSupported: boolean;
}): React.CSSProperties => {
  const {
    showTop,
    showBottom,
    showLeft,
    showRight,
    fadeSize,
    disableTransitions,
    transitionDuration,
    shouldApplyEffects,
    maskImageSupported,
  } = params;

  if (!shouldApplyEffects) {
    return {};
  }

  if (!maskImageSupported) {
    return {};
  }

  const maskImage = createMaskImage(showTop, showBottom, showLeft, showRight, fadeSize);

  const gradientLayers: string[] = [];
  if (showTop) gradientLayers.push(`linear-gradient(to bottom, rgba(0,0,0,0.15), transparent)`);
  if (showBottom) gradientLayers.push(`linear-gradient(to top, rgba(0,0,0,0.15), transparent)`);
  if (showLeft) gradientLayers.push(`linear-gradient(to right, rgba(0,0,0,0.15), transparent)`);
  if (showRight) gradientLayers.push(`linear-gradient(to left, rgba(0,0,0,0.15), transparent)`);

  const baseStyles: React.CSSProperties = {
    // Mask properties for transparency
    maskImage,
    WebkitMaskImage: maskImage,
    maskComposite: 'intersect',
    WebkitMaskComposite: 'source-in',
    maskRepeat: 'no-repeat',
    WebkitMaskRepeat: 'no-repeat',
    maskSize: '100% 100%, 100% 100%',
    WebkitMaskSize: '100% 100%, 100% 100%',
    maskPosition: '0 0, 0 0',
    WebkitMaskPosition: '0 0, 0 0',
    // Position relative for pseudo-elements
    position: 'relative',
  } as React.CSSProperties;

  if (gradientLayers.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
    (baseStyles as any)['--fade-gradients'] = gradientLayers.join(', ');
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
    (baseStyles as any)['--fade-gradient-count'] = gradientLayers.length.toString();
  }

  if (!disableTransitions) {
    const transitionValue =
      `mask ${transitionDuration}ms ease-out, -webkit-mask ${transitionDuration}ms ease-out`.trim();

    baseStyles.transition = transitionValue;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
    (baseStyles as any).WebkitTransition = transitionValue; // Safari support
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
    (baseStyles as any).MozTransition = transitionValue; // Firefox support
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
    (baseStyles as any).msTransition = transitionValue; // IE support (fallback)
  }

  return baseStyles;
};

/** Manages scroll-based fade effects on scrollable containers.*/
export function useScrollFades<T extends HTMLElement = HTMLElement>(options: UseScrollFadesOptions = {}) {
  const { threshold = 8, fadeSize = 20, transitionDuration = 200 } = options;

  const [measureRef, size] = useMeasure();
  const containerRef = useRef<T | null>(null);

  const combinedRef = useCallback(
    (node: T | null) => {
      containerRef.current = node;
      measureRef(node);
    },
    [measureRef],
  );

  const [state, setState] = useState<FadeState>({
    showTop: false,
    showBottom: false,
    showLeft: false,
    showRight: false,
  });
  const [reducedMotionPreferred, setReducedMotionPreferred] = useState(false);
  const frame = useRef<number | null>(null);

  const browserCapabilities = useMemo(() => getBrowserCapabilities(), []);

  const shouldApplyEffects = useMemo(() => {
    if (reducedMotionPreferred || browserCapabilities.prefersReducedMotion) {
      return false;
    }

    if (!browserCapabilities.supportsMaskImage) {
      return false;
    }

    return true;
  }, [reducedMotionPreferred, browserCapabilities]);

  const shouldDisableTransitions = useMemo(() => {
    if (reducedMotionPreferred || browserCapabilities.prefersReducedMotion) return true;
    if (!browserCapabilities.supportsTransitions) return true;
    return false;
  }, [reducedMotionPreferred, browserCapabilities]);

  const measure = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    const next = computeFadeState(
      el.scrollTop,
      el.scrollHeight,
      el.clientHeight,
      el.scrollLeft,
      el.scrollWidth,
      el.clientWidth,
      threshold,
    );

    setState((prev) =>
      prev.showTop === next.showTop &&
      prev.showBottom === next.showBottom &&
      prev.showLeft === next.showLeft &&
      prev.showRight === next.showRight
        ? prev
        : next,
    );
  }, [threshold]);

  const handleScroll = useCallback(() => {
    if (frame.current !== null) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = null;
      measure();
    });
  }, [measure]);

  useEffect(() => {
    setReducedMotionPreferred(prefersReducedMotion());

    const cleanup = watchMotionPreference((prefersReduced) => setReducedMotionPreferred(prefersReduced));

    return cleanup;
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    measure();

    el.addEventListener('scroll', handleScroll, { passive: true });

    const mutationObserver = new MutationObserver(measure);
    mutationObserver.observe(el, { childList: true, subtree: true });

    return () => {
      el.removeEventListener('scroll', handleScroll);
      mutationObserver.disconnect();
      if (frame.current !== null) {
        cancelAnimationFrame(frame.current);
      }
    };
  }, [handleScroll, size.width, size.height, measure]);

  const getContainerStyle = useMemo(() => {
    return (s: FadeState = state) => {
      return computeContainerStyles({
        showTop: s.showTop,
        showBottom: s.showBottom,
        showLeft: s.showLeft,
        showRight: s.showRight,
        fadeSize,
        disableTransitions: shouldDisableTransitions,
        transitionDuration,
        shouldApplyEffects,
        maskImageSupported: browserCapabilities.supportsMaskImage,
      });
    };
  }, [
    state,
    fadeSize,
    shouldDisableTransitions,
    transitionDuration,
    shouldApplyEffects,
    browserCapabilities.supportsMaskImage,
  ]);

  return {
    containerRef: combinedRef,
    getContainerStyle,
  };
}
